import React, { useState } from "react";
import {
  MapPin,
  Mail,
  Facebook,
  Instagram,
  MessageSquare,
  MousePointerClick,
} from "lucide-react";
import { FaWhatsapp, FaTiktok } from "react-icons/fa";
import { motion } from "framer-motion";

export default function ContactMap() {
  const [hoveredIndex, setHoveredIndex] = useState(null);

  const contacts = [
    {
      icon: <FaWhatsapp className="w-6 h-6 text-white" />,
      title: "WhatsApp 1",
      subtitle: "+62 812-4769-1112",
      link: "https://wa.me/6281247691112",
      color: "from-green-500 to-teal-500",
    },
    {
      icon: <FaWhatsapp className="w-6 h-6 text-white" />,
      title: "WhatsApp 2",
      subtitle: "+62 821-9930-3503",
      link: "https://wa.me/6282199303503",
      color: "from-green-600 to-lime-500",
    },
    {
      icon: <Instagram className="w-6 h-6 text-white" />,
      title: "Instagram",
      subtitle: "@pt.soney_raya",
      link: "https://www.instagram.com/pt.soney_raya",
      color: "from-pink-500 to-purple-500",
    },
    {
      icon: <Facebook className="w-6 h-6 text-white" />,
      title: "Facebook",
      subtitle: "PT Soney Raya",
      link: "https://www.facebook.com/share/1BAA91z5eG/?mibextid=wwXIfr",
      color: "from-blue-600 to-sky-500",
    },
    {
      icon: <FaTiktok className="w-6 h-6 text-white" />,
      title: "TikTok",
      subtitle: "@pt.soneyraya",
      link: "https://www.tiktok.com/@pt.soneyraya",
      color: "from-gray-900 to-gray-700",
    },
    {
      icon: <Mail className="w-6 h-6 text-white" />,
      title: "Email Resmi",
      subtitle: "soneyraya@gmail.com",
      link: "mailto:soneyraya@gmail.com",
      color: "from-yellow-500 to-orange-400",
    },
    {
      icon: <MapPin className="w-6 h-6 text-white" />,
      title: "Alamat Kantor",
      subtitle: (
        <>
          Jl. Koti, Numbai, Kota Jayapura, Papua <br />
          <span className="text-gray-500 text-sm">
            Senin - Jumat, 08:00 - 17:00 WIT
          </span>
        </>
      ),
      link: "https://maps.app.goo.gl/LFA6MytM2yQvXYGQ6",
      color: "from-emerald-500 to-teal-600",
    },
  ];

  const fadeUp = {
    hidden: { opacity: 0, y: 40 },
    visible: (i = 1) => ({
      opacity: 1,
      y: 0,
      transition: { delay: i * 0.15, duration: 0.6, ease: "easeOut" },
    }),
  };

  return (
    <section
      id="kontak"
      className="relative bg-white py-28 px-6 md:px-16 text-slate-900 overflow-hidden"
    >
      {/* 🌿 Background Pattern Animated */}
      <motion.div
        className="absolute inset-0 pointer-events-none"
        animate={{ backgroundPosition: ["0% 0%", "100% 100%", "0% 0%"] }}
        transition={{ duration: 30, repeat: Infinity, ease: "linear" }}
      >
        <div className="absolute inset-0 bg-[linear-gradient(rgba(0,128,0,0.05)_2px,transparent_2px),linear-gradient(90deg,rgba(0,128,0,0.05)_2px,transparent_2px)] bg-[size:90px_90px]" />
      </motion.div>

      {/* 🟢 Header */}
      <motion.div
        variants={fadeUp}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        className="relative z-10 text-center mb-20 max-w-3xl mx-auto"
      >
        <motion.div
          animate={{ opacity: [0.6, 1, 0.6] }}
          transition={{ duration: 2, repeat: Infinity }}
          className="inline-flex items-center gap-2 px-4 py-2 bg-green-100 border border-green-200 rounded-full backdrop-blur-sm mb-6"
        >
          <MessageSquare className="w-4 h-4 text-green-600" />
          <span className="text-green-600 text-sm font-mono uppercase tracking-wider">
            Hubungi Kami
          </span>
        </motion.div>

        <h2 className="text-5xl md:text-7xl font-black mb-6 tracking-tight leading-tight">
          <span className="text-slate-900">Kontak </span>
          <span className="text-transparent bg-clip-text bg-gradient-to-r from-green-500 via-teal-400 to-green-600">
            Perusahaan
          </span>
        </h2>

        <motion.div
          animate={{ scaleX: [0.9, 1.1, 0.9] }}
          transition={{ duration: 3, repeat: Infinity }}
          className="h-1 w-32 bg-gradient-to-r from-green-500 to-teal-500 rounded-full mx-auto mb-8"
        ></motion.div>

        <p className="text-lg text-gray-600 leading-relaxed font-light">
          Terhubung langsung dengan kami melalui WhatsApp, Instagram, atau Email
          resmi.
        </p>
      </motion.div>

      {/* 🧭 Contact Cards */}
      <div className="relative z-10 max-w-6xl mx-auto grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
        {contacts.map((item, index) => {
          const isHovered = hoveredIndex === index;
          return (
            <motion.a
              key={index}
              variants={fadeUp}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              custom={index}
              whileHover={{ scale: 1.04, y: -4 }}
              transition={{ type: "spring", stiffness: 200, damping: 18 }}
              href={item.link}
              target="_blank"
              rel="noopener noreferrer"
              onMouseEnter={() => setHoveredIndex(index)}
              onMouseLeave={() => setHoveredIndex(null)}
              className="group relative overflow-hidden border border-gray-200 rounded-3xl bg-white shadow-md hover:shadow-xl transition-all duration-500 cursor-pointer transform-gpu"
            >
              {/* Glow */}
              <motion.div
                animate={{ opacity: [0, 0.3, 0] }}
                transition={{ duration: 3, repeat: Infinity }}
                className={`absolute -inset-1 bg-gradient-to-r ${item.color} rounded-3xl blur-xl opacity-0 group-hover:opacity-25 transition duration-500`}
              ></motion.div>

              {/* Content */}
              <div className="relative p-8 flex flex-col items-center text-center z-10 transition-transform duration-500 group-hover:scale-[1.02]">
                <motion.div
                  whileHover={{ rotate: 3, scale: 1.15 }}
                  transition={{ type: "spring", stiffness: 250 }}
                  className={`w-16 h-16 rounded-2xl flex items-center justify-center bg-gradient-to-br ${item.color} mb-5 shadow-lg`}
                >
                  {item.icon}
                </motion.div>
                <h4 className="text-lg font-bold text-slate-900 mb-2">
                  {item.title}
                </h4>
                <p className="text-gray-600 text-sm leading-relaxed mb-3">
                  {item.subtitle}
                </p>
                <motion.div
                  initial={{ opacity: 0, y: 6 }}
                  animate={{
                    opacity: isHovered ? 1 : 0,
                    y: isHovered ? 0 : 6,
                  }}
                  transition={{ duration: 0.4 }}
                  className="flex items-center gap-2 text-green-600 text-sm font-medium mt-3"
                >
                  <MousePointerClick className="w-4 h-4" />
                  Klik untuk membuka
                </motion.div>
              </div>
            </motion.a>
          );
        })}
      </div>

      {/* 🗺️ Map Section Animated */}
      <motion.div
        variants={fadeUp}
        initial="hidden"
        whileInView="visible"
        transition={{ delay: 0.2, duration: 0.6 }}
        viewport={{ once: true }}
        className="relative z-10 max-w-6xl mx-auto mt-24"
      >
        <motion.div
          whileHover={{ scale: 1.01 }}
          transition={{ type: "spring", stiffness: 150 }}
          className="relative rounded-3xl overflow-hidden border border-gray-200 shadow-lg hover:shadow-2xl transition-shadow duration-500 transform-gpu"
        >
          <iframe
            src="https://www.google.com/maps?q=-2.547130,140.713259&hl=id&z=15&output=embed"
            width="100%"
            height="420"
            style={{ border: 0 }}
            allowFullScreen
            loading="lazy"
            title="Lokasi Kantor PT Soney Raya"
          ></iframe>
        </motion.div>
      </motion.div>
    </section>
  );
}
